import { ChangeEvent, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/system';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { Controller, useForm } from 'react-hook-form';
import { CompanyFiscalYear } from 'api';
import { CustomTextField, WebsiteTextField, YearList, YearListProps } from 'components';
import CurrencyList from 'components/CurrencyList';
import { CurrencyListOption } from 'components/CurrencyList/CurrencyList';
import {
  FUND_INVESTMENTS_DEFAULT_CURRENCY,
  FUND_INVESTMENTS_FORM_LABELS,
} from 'pages/FundInvestments/common/constants';
import { useDialogFormsStore } from 'store';
import { getFieldError, getLocaleAgnosticDate, getStringValue, gridShortDate, reconstructDate } from 'utilities';
import { FUND_INVESTMENT_FORM_SCHEMA } from './useFundInvestmentForm.schema';
import useFundInvestmentFormStyles from './useFundInvestmentForm.styles';
import {
  FundInvestmentFormSchema,
  FundInvestmentFormState,
  UseFundInvestmentFormParams,
  UseFundInvestmentFormReturn,
} from './useFundInvestmentForm.types';

const FUND_INVESTMENT_FORM_DEFAULT_VALUES: FundInvestmentFormSchema = {
  name: '',
  legal_company_name: '',
  website: '',
  captable_currency: FUND_INVESTMENTS_DEFAULT_CURRENCY,
  fiscal_year_end: { end_month: 12, end_day: 31 },
  vintage_year: null,
};

const useFundInvestmentForm = (params: UseFundInvestmentFormParams): UseFundInvestmentFormReturn => {
  const { fundInvestment } = params;

  const shouldUpdateFundInvestment = useDialogFormsStore(state => state.shouldUpdateFundInvestment);

  const classes = useFundInvestmentFormStyles();

  // Form state
  const {
    control,
    formState: { errors, isValid },
    watch,
  } = useForm<FundInvestmentFormSchema>({
    mode: 'onChange',
    defaultValues: fundInvestment ?? FUND_INVESTMENT_FORM_DEFAULT_VALUES,
    resolver: yupResolver(FUND_INVESTMENT_FORM_SCHEMA),
  });

  const currentValues = watch();

  const formState = useMemo<FundInvestmentFormState>(
    () => ({ errors, isValid, values: currentValues }) as FundInvestmentFormState,
    [currentValues, errors, isValid]
  );

  const FundInvestmentForm = (
    <Grid container>
      <Grid className={classes.section} container justifyContent="space-between" spacing={2}>
        {/* Name */}
        <Grid size={{ xs: 8 }}>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState: { invalid, error } }) => {
              const { name, onBlur, onChange, value } = field;

              const label = getStringValue(FUND_INVESTMENTS_FORM_LABELS?.[name]);

              return (
                <CustomTextField
                  dbErrors={undefined}
                  formErrors={getFieldError({ error, invalid, name, label })}
                  fullWidth
                  id={name}
                  label={label}
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  required
                  type="text"
                  value={value}
                  variant="standard"
                />
              );
            }}
          />
        </Grid>

        {/* Fiscal year end */}
        <Grid size={{ xs: 4 }}>
          <Controller
            name="fiscal_year_end"
            control={control}
            render={({ field, fieldState: { invalid, error } }) => {
              const { name, onBlur, onChange, value } = field;

              const label = getStringValue(FUND_INVESTMENTS_FORM_LABELS?.[name]);

              const handleOnChange = (date: Date | null) => {
                if (date) {
                  // Locale-agnostic date
                  const { month, day } = getLocaleAgnosticDate({ date });

                  const fiscalYearEnd: CompanyFiscalYear = {
                    ...formState.values.fiscal_year_end,
                    end_month: Number(month),
                    end_day: Number(day),
                  };

                  onChange({
                    target: {
                      name,
                      value: fiscalYearEnd,
                    },
                  } as ChangeEvent<HTMLInputElement>);
                }
              };

              const formattedDate = gridShortDate(
                reconstructDate(
                  value?.end_month ?? FUND_INVESTMENT_FORM_DEFAULT_VALUES.fiscal_year_end.end_month,
                  value?.end_day ?? FUND_INVESTMENT_FORM_DEFAULT_VALUES.fiscal_year_end.end_day
                )
              ) as unknown as Date;

              return (
                <MobileDatePicker
                  format="MM/dd"
                  label={label}
                  name={name}
                  onChange={handleOnChange}
                  openTo={shouldUpdateFundInvestment ? 'day' : 'month'}
                  slotProps={{
                    textField: {
                      error: invalid,
                      helperText: invalid
                        ? getStringValue(getFieldError({ error, invalid, name, label })?.[name]?.join(','))
                        : '',
                      id: name,
                      onBlur,
                      required: true,
                      variant: 'standard',
                    },
                  }}
                  value={new Date(formattedDate)}
                  views={['year', 'month', 'day']}
                />
              );
            }}
          />
        </Grid>
      </Grid>

      <Grid className={classes.section} container justifyContent="space-between" spacing={2}>
        {/* Legal name */}
        <Grid size={{ xs: 8 }}>
          <Controller
            name="legal_company_name"
            control={control}
            render={({ field, fieldState: { invalid, error } }) => {
              const { name, onBlur, onChange, value } = field;

              const label = getStringValue(FUND_INVESTMENTS_FORM_LABELS?.[name]);

              return (
                <CustomTextField
                  formErrors={getFieldError({ error, invalid, name, label })}
                  fullWidth
                  id={name}
                  label={label}
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  onFocus={undefined}
                  type="text"
                  value={getStringValue(value)}
                  variant="standard"
                />
              );
            }}
          />
        </Grid>

        {/* Currency */}
        <Grid size={{ xs: 4 }}>
          <Controller
            name="captable_currency"
            control={control}
            render={({ field }) => {
              const { name, onChange, value } = field;

              const label = getStringValue(FUND_INVESTMENTS_FORM_LABELS?.[name]);
              const handleOnChange = (_event: ChangeEvent<unknown>, option: CurrencyListOption) =>
                onChange(option.value);

              return <CurrencyList label={label} formCurrency={value} handleCurrencyChange={handleOnChange} />;
            }}
          />
        </Grid>
      </Grid>

      <Grid className={classes.section} container justifyContent="space-between" spacing={2}>
        {/* Website */}
        <Grid size={{ xs: 8 }}>
          <Controller
            name="website"
            control={control}
            render={({ field, fieldState: { invalid, error } }) => {
              const { name, onBlur, onChange, value } = field;

              const label = getStringValue(FUND_INVESTMENTS_FORM_LABELS?.[name]);

              return (
                <WebsiteTextField
                  formErrors={getFieldError({ error, invalid, name, label })}
                  fullWidth
                  id={name}
                  label={label}
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  type="text"
                  value={value}
                  variant="standard"
                />
              );
            }}
          />
        </Grid>

        {/* Vintage year */}
        <Grid size={{ xs: 4 }}>
          <Controller
            name="vintage_year"
            control={control}
            render={({ field }) => {
              const { name, onChange, value } = field;

              const label = getStringValue(FUND_INVESTMENTS_FORM_LABELS?.[name]);
              const handleOnChange = (_event: ChangeEvent<unknown>, option: YearListProps) => onChange(option.value);

              return <YearList label={label} onChange={handleOnChange} value={value} />;
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );

  return {
    formState,
    FundInvestmentForm,
  };
};

export default useFundInvestmentForm;
